import React, { useState, useCallback } from 'react';
import {
    Table, Tag, Button, Space, Input, Typography,
    Tooltip, Collapse, Divider, Row, Col, Statistic,
    Alert, message, Badge,
} from 'antd';
import {
    SearchOutlined,
    TrophyOutlined, RiseOutlined,
} from '@ant-design/icons';
import AdminLayout from '../Layout/AdminLayout';
import axiosInstance from '../../utils/axiosConfig';

const { Text } = Typography;
const { Search } = Input;

// ── Hằng số ───────────────────────────────────────────────────────────────────

const COMBO_COLOR = { A: 'blue', B: 'green', C: 'volcano', D: 'purple', X: 'orange' };
const getComboColor = (code = '') => COMBO_COLOR[code[0]] ?? 'default';

const AREA_TAG_COLOR = { KV1: 'blue', 'KV2-NT': 'cyan', KV2: 'green', KV3: 'default' };

const SUBJECT_LABEL = {
    math:            'Toán',
    literature:      'Ngữ văn',
    english:         'Ngoại ngữ',
    physics:         'Vật lý',
    chemistry:       'Hóa học',
    biology:         'Sinh học',
    history:         'Lịch sử',
    geography:       'Địa lý',
    civic_education: 'GDCD',
    technology:      'Công nghệ',
    computer_science:'Tin học',
};

// ── Bảng điểm thi THPT ───────────────────────────────────────────────────────

const ThptScoreTable = ({ thptScores }) => {
    if (!thptScores) return null;

    const subjects = Object.entries(thptScores);
    const hasAnyScore = subjects.some(([, v]) => v !== null);
    if (!hasAnyScore) return null;

    const dataRow = { key: 'row' };
    subjects.forEach(([key, val]) => {
        dataRow[key] = val !== null ? parseFloat(val) : null;
    });

    const columns = subjects.map(([key]) => ({
        title: SUBJECT_LABEL[key] ?? key,
        key,
        dataIndex: key,
        align: 'center',
        width: 90,
        render: (v) =>
            v !== null && v !== 0
                ? <Text strong style={{ color: '#1890ff' }}>{v.toFixed(2)}</Text>
                : <Text type="secondary">—</Text>,
    }));

    return (
        <div style={{ marginBottom: 16 }}>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                Điểm thi THPT quốc gia
            </Text>
            <Table
                dataSource={[dataRow]}
                columns={columns}
                pagination={false}
                size="small"
                bordered
                scroll={{ x: 'max-content' }}
            />
        </div>
    );
};

// ── Expandable: chi tiết tất cả nguyện vọng của 1 thí sinh ───────────────────

const StudentScoreDetail = ({ record }) => {
    const { bonus_points: bp, priority_points: pp, admissions } = record;

    const admissionPanels = admissions.map((adm) => {
        const combosWithMeta = (adm.combinations ?? []).map(c => ({
            ...c,
            _bestCombo:  adm.best_combo,
            _benchmark:  adm.benchmark ?? null,
        }));

        const benchmarkTitle = adm.benchmark
            ? `Điểm chuẩn ${adm.benchmark.year}`
            : 'Điểm chuẩn';

        const comboColumns = [
            {
                title: 'Tổ hợp',
                dataIndex: 'code',
                width: 140,
                render: (code, row) => (
                    <Space size={4} style={{ whiteSpace: 'nowrap' }}>
                        <Tag color={getComboColor(code)} style={{ minWidth: 44, textAlign: 'center', marginRight: 0 }}>
                            {code}
                        </Tag>
                        {code === row._bestCombo && (
                            <Text type="success" style={{ fontSize: 11 }}>★ Cao nhất</Text>
                        )}
                    </Space>
                ),
            },
            {
                title: 'Tên tổ hợp',
                dataIndex: 'name',
                ellipsis: true,
            },
            {
                title: benchmarkTitle,
                dataIndex: '_benchmark',
                width: 110,
                align: 'center',
                render: (bm) => bm
                    ? <Text style={{ color: '#595959' }}>{bm.cutoff_score.toFixed(2)}</Text>
                    : <Text type="secondary">—</Text>,
            },
            {
                title: 'Điểm THM',
                dataIndex: 'thm_score',
                width: 100,
                align: 'center',
                render: (v, row) =>
                    row.note
                        ? <Text type="secondary" style={{ fontSize: 11 }}>{row.note}</Text>
                        : <Text strong>{v?.toFixed(2) ?? '—'}</Text>,
            },
            {
                title: 'Điểm xét tuyển',
                dataIndex: 'total_score',
                width: 130,
                align: 'center',
                render: (v, row) => {
                    if (row.note || v === null) return <Text type="secondary">—</Text>;
                    const isBest = row.code === row._bestCombo;
                    return (
                        <Text strong style={{ color: isBest ? '#1890ff' : undefined, fontSize: isBest ? 15 : 13 }}>
                            {v.toFixed(2)}
                        </Text>
                    );
                },
            },
        ];

        const hasScore = adm.best_score !== null;

        return {
            key: adm.priority,
            label: (
                <Space>
                    <Text strong>NV{adm.priority}: {adm.subject_name}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>({adm.subject_code})</Text>
                    {hasScore ? (
                        <>
                            <Text type="secondary">→ Điểm cao nhất:</Text>
                            <Text strong style={{ color: '#1890ff' }}>{adm.best_score?.toFixed(2)}</Text>
                            <Tag color={getComboColor(adm.best_combo)}>{adm.best_combo}</Tag>
                            {adm.benchmark && (() => {
                                const passed = adm.best_score >= adm.benchmark.cutoff_score;
                                const diff = (adm.best_score - adm.benchmark.cutoff_score).toFixed(2);
                                return (
                                    <Tag color={passed ? 'success' : 'error'} style={{ fontSize: 12 }}>
                                        Điểm chuẩn {adm.benchmark.year}: {adm.benchmark.cutoff_score.toFixed(2)}
                                        {' '}
                                    </Tag>
                                );
                            })()}
                        </>
                    ) : (
                        <Tag color="warning">Thiếu dữ liệu</Tag>
                    )}
                </Space>
            ),
            children: combosWithMeta.length === 0 ? (
                <Alert type="warning" showIcon message="Ngành này chưa được gán tổ hợp xét tuyển." />
            ) : (
                <>
                    <Table
                        dataSource={combosWithMeta}
                        columns={comboColumns}
                        rowKey="code"
                        size="small"
                        pagination={false}
                        style={{ marginBottom: 8 }}
                    />
                    {hasScore && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            THM ({adm.best_combo}): <strong>{adm.best_thm?.toFixed(2)}</strong>
                            {' + '}Điểm cộng: <strong>+{bp.total.toFixed(2)}</strong>
                            {' + '}Điểm ưu tiên: <strong>+{(adm.best_effective_priority ?? 0).toFixed(2)}</strong>
                            {' = '}
                            <Text strong style={{ color: '#1890ff' }}>{adm.best_score?.toFixed(2)}</Text>
                        </Text>
                    )}
                </>
            ),
        };
    });

    return (
        <div style={{ padding: '8px 24px 16px' }}>
            {/* Điểm thi THPT quốc gia */}
            <ThptScoreTable thptScores={record.thpt_exam_scores} />

            {/* Điểm cộng & ưu tiên */}
            <Row gutter={32} style={{ marginBottom: 12 }}>
                <Col>
                    <Space>
                        <TrophyOutlined style={{ color: '#faad14' }} />
                        <Text>Điểm cộng:</Text>
                        <Text strong style={{ color: '#52c41a' }}>+{bp.total.toFixed(2)}</Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            (IELTS +{bp.ielts.toFixed(2)} / HSG +{bp.hsg_prize.toFixed(2)} / Học lực +{bp.academic_ranking.toFixed(2)})
                        </Text>
                    </Space>
                </Col>
                <Col>
                    <Space>
                        <RiseOutlined style={{ color: '#722ed1' }} />
                        <Text>Điểm ưu tiên:</Text>
                        <Text strong style={{ color: '#722ed1' }}>+{pp.total.toFixed(2)}</Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            (Khu vực +{pp.area.toFixed(2)} / Đối tượng +{pp.group.toFixed(2)})
                        </Text>
                    </Space>
                </Col>
            </Row>

            {/* Chi tiết từng nguyện vọng */}
            {admissions.length === 0 ? (
                <Alert type="warning" showIcon message="Thí sinh chưa có nguyện vọng." />
            ) : (
                <Collapse size="small" items={admissionPanels} />
            )}
        </div>
    );
};

// ── Component chính ───────────────────────────────────────────────────────────

const AdminAdmissionScores = () => {
    const [data, setData]         = useState([]);
    const [loading, setLoading]   = useState(false);
    const [searched, setSearched] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [expandedRows, setExpandedRows] = useState([]);

    const fetchScores = useCallback(async (identification) => {
        setLoading(true);
        setExpandedRows([]);
        try {
            const res = await axiosInstance.get('/api/v1/admin/admission-scores', {
                params: { identification },
            });
            if (res.data?.success) {
                setData(res.data.data ?? []);
                setSearched(true);
                if ((res.data.data ?? []).length === 0) {
                    message.info('Không tìm thấy thí sinh với số CCCD này.');
                }
            }
        } catch {
            message.error('Không thể tải dữ liệu. Vui lòng thử lại.');
        } finally {
            setLoading(false);
        }
    }, []);

    const handleSearch = (value) => {
        const trimmed = value.trim();
        if (!trimmed) return;
        setSearchText(trimmed);
        fetchScores(trimmed);
    };

    const handleReset = () => {
        setSearchText('');
        setData([]);
        setSearched(false);
        setExpandedRows([]);
    };

    // ── Cột bảng chính ────────────────────────────────────────────────────
    const columns = [
        {
            title: 'Mã hồ sơ',
            dataIndex: 'student_code',
            key: 'student_code',
            width: 120,
            sorter: (a, b) => a.student_code?.localeCompare(b.student_code),
            render: (v) => <Text code>{v}</Text>,
        },
        {
            title: 'Họ và tên',
            dataIndex: 'full_name',
            key: 'full_name',
            sorter: (a, b) => a.full_name?.localeCompare(b.full_name),
        },
        {
            title: 'Khu vực',
            dataIndex: 'admission_area',
            key: 'admission_area',
            width: 110,
            filters: [
                { text: 'KV1', value: 'KV1' },
                { text: 'KV2-NT', value: 'KV2-NT' },
                { text: 'KV2', value: 'KV2' },
                { text: 'KV3', value: 'KV3' },
            ],
            onFilter: (v, r) => r.admission_area === v,
            render: (v) => v
                ? <Tag color={AREA_TAG_COLOR[v] ?? 'default'}>{v}</Tag>
                : <Text type="secondary">—</Text>,
        },
        {
            title: (
                <Tooltip title="IELTS + Giải HSG + Học lực">
                    <Space><TrophyOutlined style={{ color: '#faad14' }} />Điểm cộng</Space>
                </Tooltip>
            ),
            key: 'bonus',
            width: 110,
            align: 'center',
            sorter: (a, b) => a.bonus_points.total - b.bonus_points.total,
            render: (_, r) => r.bonus_points.total > 0
                ? <Text strong style={{ color: '#52c41a' }}>+{r.bonus_points.total.toFixed(2)}</Text>
                : <Text type="secondary">0</Text>,
        },
        {
            title: (
                <Tooltip title="Khu vực + Đối tượng">
                    <Space><RiseOutlined style={{ color: '#722ed1' }} />Điểm ưu tiên</Space>
                </Tooltip>
            ),
            key: 'priority',
            width: 120,
            align: 'center',
            sorter: (a, b) => a.priority_points.total - b.priority_points.total,
            render: (_, r) => r.priority_points.total > 0
                ? <Text strong style={{ color: '#722ed1' }}>+{r.priority_points.total.toFixed(2)}</Text>
                : <Text type="secondary">0</Text>,
        },
        {
            title: 'NV1 — Điểm & Tổ hợp tốt nhất',
            key: 'nv1',
            sorter: (a, b) => {
                const sa = a.admissions?.find(x => x.priority === 1)?.best_score ?? -1;
                const sb = b.admissions?.find(x => x.priority === 1)?.best_score ?? -1;
                return sa - sb;
            },
            render: (_, r) => {
                const nv1 = r.admissions?.find(x => x.priority === 1);
                if (!nv1) return <Text type="secondary">—</Text>;
                return (
                    <Space>
                        {nv1.best_score !== null ? (
                            <Text strong style={{ color: '#1890ff', fontSize: 15 }}>
                                {nv1.best_score.toFixed(2)}
                            </Text>
                        ) : (
                            <Text type="secondary">—</Text>
                        )}
                        {nv1.best_combo && (
                            <Tag color={getComboColor(nv1.best_combo)}>{nv1.best_combo}</Tag>
                        )}
                        <Text type="secondary" style={{ fontSize: 11 }} ellipsis>
                            {nv1.subject_name}
                        </Text>
                    </Space>
                );
            },
        },
        {
            title: 'Số NV',
            key: 'nv_count',
            width: 75,
            align: 'center',
            sorter: (a, b) => (a.admissions?.length ?? 0) - (b.admissions?.length ?? 0),
            render: (_, r) => (
                <Badge count={r.admissions?.length ?? 0} showZero color="#1890ff" />
            ),
        },
    ];

    return (
        <AdminLayout title="Điểm xét tuyển thí sinh">
            {/* ── Thanh tìm kiếm ── */}
            <Space style={{ marginBottom: 16 }} wrap>
                <Search
                    placeholder="Nhập số CCCD để tra cứu..."
                    allowClear
                    enterButton={<><SearchOutlined /> Tra cứu</>}
                    style={{ width: 300 }}
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                    onSearch={handleSearch}
                    loading={loading}
                />
                {searched && (
                    <Button onClick={handleReset}>Xoá kết quả</Button>
                )}
                {searched && data.length > 0 && (
                    <Text type="secondary">
                        Tìm thấy <strong>{data.length}</strong> thí sinh
                    </Text>
                )}
            </Space>

            {!searched ? (
                <Alert
                    type="info"
                    showIcon
                    message="Nhập số CCCD của thí sinh để tra cứu điểm xét tuyển."
                />
            ) : (
                <Table
                    columns={columns}
                    dataSource={data}
                    rowKey="student_code"
                    loading={loading}
                    size="small"
                    scroll={{ x: 900 }}
                    pagination={{
                        pageSize: 20,
                        showTotal: (t) => `Tổng ${t} thí sinh`,
                        showSizeChanger: false,
                    }}
                    expandable={{
                        expandedRowKeys: expandedRows,
                        onExpand: (expanded, record) => {
                            setExpandedRows(
                                expanded
                                    ? [...expandedRows, record.student_code]
                                    : expandedRows.filter(k => k !== record.student_code)
                            );
                        },
                        expandedRowRender: (record) => (
                            <StudentScoreDetail record={record} />
                        ),
                        rowExpandable: (record) => record.admissions?.length > 0,
                    }}
                />
            )}
        </AdminLayout>
    );
};

export default AdminAdmissionScores;
